import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import NewPage from '../../../layouts/page/NewPage';
import FormSection from '../../../components/Common/Form/FormSection';
import AsteriskRequired from '../../../components/Common/AsteriskRequired';
import CustomSelect from '../../../components/Forms/Element/CustomSelect';
import SaaSBillingService from '../../../../services/superadmin/SaaSBillingService';
import { errorAlert, successAlert } from '../../../components/alert/defaultAlert';

const SaaSContractForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [companies, setCompanies] = useState([]);
    const [plans, setPlans] = useState([]);
    const today = moment().format('YYYY-MM-DD');
    const [form, setForm] = useState({ companyId: '', planId: '', gracePeriodDays: 5, firstDueDate: today });
    // Contrato com assinatura ativa no provedor: salvar só atualiza plano e tolerância,
    // então a data da primeira cobrança não se aplica e o campo fica escondido.
    const [hasSubscription, setHasSubscription] = useState(false);
    // Plano que o contrato tinha ao abrir a tela. Comparar com o escolhido é o que
    // identifica uma troca de plano, e só nela a fatura em aberto entra em jogo.
    const [originalPlanId, setOriginalPlanId] = useState(null);
    const [preview, setPreview] = useState(null);
    const [updateOpenInvoice, setUpdateOpenInvoice] = useState(false);

    useEffect(() => {
        Promise.all([SaaSBillingService.companies(), SaaSBillingService.plans()])
            .then(([companiesResponse, plansResponse]) => {
                setCompanies(companiesResponse.data || []);
                setPlans(plansResponse.data || []);
            }).catch(error => errorAlert(error));
        if (id) {
            SaaSBillingService.contracts().then(response => {
                const contract = response.data.find(item => String(item.id) === String(id));
                if (contract) {
                    setForm(current => ({ ...current, companyId: contract.company?.id || '', planId: contract.plan?.id || '', gracePeriodDays: contract.gracePeriodDays ?? 5 }));
                    // Cancelar limpa o vínculo; sem ele, salvar recontrata e emite a primeira fatura.
                    setHasSubscription(Boolean(contract.providerSubscriptionId));
                    setOriginalPlanId(contract.plan?.id ?? null);
                }
            }).catch(error => errorAlert(error));
        }
    }, [id]);

    // Busca a prévia assim que o operador escolhe um plano diferente do atual. Mostrar
    // valores e consequência antes de salvar evita a troca às cegas: o acesso aos módulos
    // muda na hora, mas a fatura já emitida só acompanha se ele pedir.
    const planChanged = Boolean(id) && originalPlanId && Number(form.planId) !== Number(originalPlanId);

    useEffect(() => {
        if (!planChanged || !form.companyId || !form.planId) {
            setPreview(null);
            setUpdateOpenInvoice(false);
            return;
        }
        SaaSBillingService.planChangePreview({
            companyId: Number(form.companyId),
            planId: Number(form.planId)
        })
            .then(response => setPreview(response.data || null))
            .catch(() => setPreview(null));
    }, [planChanged, form.companyId, form.planId]);

    const submit = async event => {
        event.preventDefault();
        try {
            const payload = { companyId: Number(form.companyId), planId: Number(form.planId), gracePeriodDays: Number(form.gracePeriodDays) };
            // Só vai quando uma assinatura nova será criada; vazio, o servidor usa hoje.
            if (!hasSubscription && form.firstDueDate) payload.firstDueDate = form.firstDueDate;
            // Só faz sentido na troca de plano de um contrato com fatura em aberto.
            if (planChanged && preview?.hasOpenInvoice) payload.updateOpenInvoice = updateOpenInvoice;
            if (id) await SaaSBillingService.updateContract(payload); else await SaaSBillingService.createContract(payload);
            successAlert(id ? 'Contrato atualizado com sucesso.' : 'Contrato criado com sucesso.', () => navigate('/superadmin/saas-billing/contracts'));
        } catch (error) {
            errorAlert(error?.response?.data?.message || error?.message || 'Não foi possível salvar o contrato.');
        }
    };

    const formatMoney = value => `R$ ${Number(value || 0).toFixed(2).replace('.', ',')}`;
    const formatDate = value => (value ? moment(value).format('DD/MM/YYYY') : '-');

    const companyOptions = companies.map(company => ({ value: company.id, label: company.name }));
    const planOptions = plans.map(plan => ({ value: plan.id, label: `${plan.name} - R$ ${plan.price}` }));

    const content = (
        <div className="form-layout form-layout--spaced">
            <FormSection
                title={id ? 'Editar contrato SaaS' : 'Novo contrato SaaS'}
                description="Associe uma empresa a um plano recorrente e configure a tolerância de pagamento."
                icon="fa-solid fa-file-signature">
                <Row className="g-3">
                    <Col md={6}>
                        <Form.Label>Empresa <AsteriskRequired /></Form.Label>
                        <CustomSelect
                            placeholder="Selecione a empresa"
                            options={companyOptions}
                            value={companyOptions.find(option => option.value === Number(form.companyId)) || null}
                            onChange={option => setForm({ ...form, companyId: option?.value || '' })}
                        />
                    </Col>
                    <Col md={6}>
                        <Form.Label>Plano <AsteriskRequired /></Form.Label>
                        <CustomSelect
                            placeholder="Selecione o plano"
                            options={planOptions}
                            value={planOptions.find(option => option.value === Number(form.planId)) || null}
                            onChange={option => setForm({ ...form, planId: option?.value || '' })}
                        />
                    </Col>
                    <Col md={6}>
                        <Form.Label>Dias de tolerância <AsteriskRequired /></Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            value={form.gracePeriodDays}
                            onChange={event => setForm({ ...form, gracePeriodDays: event.target.value })}
                            required
                        />
                    </Col>
                    {!hasSubscription && (
                        <Col md={6}>
                            <Form.Label>Primeira cobrança em <AsteriskRequired /></Form.Label>
                            <Form.Control
                                type="date"
                                min={today}
                                value={form.firstDueDate}
                                onChange={event => setForm({ ...form, firstDueDate: event.target.value })}
                                required
                            />
                            <Form.Text className="text-muted">
                                Vencimento da primeira fatura. As seguintes seguem o ciclo do plano a partir desta data.
                            </Form.Text>
                        </Col>
                    )}
                </Row>
            </FormSection>

            {/* Prévia da troca de plano: aparece só quando o operador escolhe um plano
                diferente do atual, com os dois valores lado a lado e a decisão sobre a
                fatura do ciclo corrente. */}
            {planChanged && preview && (
                <FormSection
                    title={preview.direction === 'UPGRADE' ? 'Upgrade de plano'
                        : preview.direction === 'DOWNGRADE' ? 'Downgrade de plano' : 'Troca de plano'}
                    description="Confira o que muda antes de salvar."
                    icon="fa-solid fa-arrow-up-right-dots">
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Label>Plano atual</Form.Label>
                            <p className="mb-0">
                                {preview.currentPlanName || '-'}
                                {preview.currentPrice != null && ` (${formatMoney(preview.currentPrice)})`}
                            </p>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Plano novo</Form.Label>
                            <p className="mb-0">
                                {preview.newPlanName || '-'}
                                {preview.newPrice != null && ` (${formatMoney(preview.newPrice)})`}
                            </p>
                        </Col>
                        <Col md={12}>
                            <p className="text-muted mb-0">
                                O acesso aos módulos do plano novo passa a valer assim que o contrato for salvo.
                            </p>
                        </Col>
                        {preview.hasOpenInvoice ? (
                            <Col md={12}>
                                <Form.Check
                                    type="checkbox"
                                    id="saas-update-open-invoice"
                                    checked={updateOpenInvoice}
                                    onChange={event => setUpdateOpenInvoice(event.target.checked)}
                                    label={`Atualizar a fatura em aberto (vence em ${formatDate(preview.openInvoiceDueDate)}${
                                        preview.openInvoiceValue != null ? `, hoje ${formatMoney(preview.openInvoiceValue)}` : ''
                                    }) para o valor do plano novo`}
                                />
                                <Form.Text className="text-muted">
                                    {updateOpenInvoice
                                        ? 'A fatura deste ciclo passa a ser cobrada pelo valor do plano novo.'
                                        : 'A fatura deste ciclo continua com o valor atual, e o plano novo só é cobrado na próxima.'}
                                </Form.Text>
                            </Col>
                        ) : (
                            <Col md={12}>
                                <Form.Text className="text-muted">
                                    Não há fatura em aberto neste contrato, então o valor do plano novo vale a partir da próxima cobrança.
                                </Form.Text>
                            </Col>
                        )}
                    </Row>
                </FormSection>
            )}
        </div>
    );

    return (
        <NewPage
            motherMenu="Cobrança SaaS"
            childMenu="Contratos SaaS"
            activeMenu={id ? 'Editar' : 'Novo'}
            cancelRoute="/superadmin/saas-billing/contracts"
            handleSubmit={submit}
            child={content}
        />
    );
};
export default SaaSContractForm;
